import React, { useState, useEffect, FC } from "react";
import { Icon, TooltipComponent } from "../../components/Component";
import FormatDate from "../masters/DateFormater";
import { useNavigate } from "react-router";
import { formatCurrency } from "../../utils/Utils";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { useSelector } from "react-redux";
import {
  mockWorkOrdersData,
  mockWorkOrderWidgetData,
  mockWorkOrderStatusOptions,
  mockContractorOptions,
  mockContractTypeOptions,
} from "./mockData/viewWorkOrderData";
import { WorkOrderData, WorkOrderStatus } from "./types";
import WorkOrderAside from "./workOrderAside";
import {
  Drawer,
  Button,
  Badge,
  Select,
  Input,
  DatePicker,
  Form,
  Row,
  Col,
  Space,
  Divider,
  message,
} from "antd";
import {
  FilterOutlined,
  SearchOutlined,
  ReloadOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import data from "../app/file-manager/Data";
import Swal from "sweetalert2";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface FilterState {
  workOrderNumber?: string;
  workOrderTitle?: string;
  status?: string[];
  siteId?: string[];
  contractorName?: string[];
  contractType?: string[];
  createdDateRange?: [any, any] | null;
  releasedDateRange?: [any, any] | null;
  grossValueRange?: { min?: number; max?: number };
  workOrderOwner?: string;
}

const ViewWorkOrder: FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [widgetData, setWidgetData] = useState<any>({});
  const [workOrderStatus, setWorkOrderStatus] = useState<any>([]);
  const [contractorOptions, setContractorOptions] = useState<any>([]);
  const [contractTypeOptions, setContractTypeOptions] = useState<any>([]);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [selectedKPIFilter, setSelectedKPIFilter] = useState<string>("status");
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [refreshToggle, setRefreshToggle] = useState(false);

  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [form] = Form.useForm();

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "object" && value !== null) {
          if ("min" in value && value.min !== undefined) count++;
          if ("max" in value && value.max !== undefined) count++;
        } else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  // KPI Filter Categories
  const kpiFilterOptions = [
    { label: "By Status", value: "status" },
    { label: "By Value", value: "value" },
    { label: "By Time", value: "time" },
  ];

  const getStatusColor = (
    status: WorkOrderStatus
  ): { color: string; bgColor: string } => {
    switch (status) {
      case WorkOrderStatus.Draft:
        return { color: "#6C757D", bgColor: "#F8F9FA" };
      case WorkOrderStatus.PendingApproval:
        return { color: "#FD7E14", bgColor: "#FFF3E0" };
      case WorkOrderStatus.Rejected:
        return { color: "#DC3545", bgColor: "#FFEBEE" };
      case WorkOrderStatus.Approved:
        return { color: "#28A745", bgColor: "#E8F5E8" };
      case WorkOrderStatus.Released:
        return { color: "#17A2B8", bgColor: "#E0F7FA" };
      case WorkOrderStatus.ContractorConfirmed:
        return { color: "#20C997", bgColor: "#E0F2F1" };
      case WorkOrderStatus.InExecution:
        return { color: "#007BFF", bgColor: "#E3F2FD" };
      case WorkOrderStatus.OnHold:
        return { color: "#FFC107", bgColor: "#FFFDE7" };
      case WorkOrderStatus.VariationPending:
        return { color: "#E83E8C", bgColor: "#FCE4EC" };
      case WorkOrderStatus.ChangesRequested:
        return { color: "orange", bgColor: "#f5eae5ff" };
      case WorkOrderStatus.Completed:
        return { color: "#198754", bgColor: "#D4EDDA" };
      case WorkOrderStatus.Closed:
        return { color: "#495057", bgColor: "#E9ECEF" };
      case WorkOrderStatus.Cancelled:
        return { color: "#DC3545", bgColor: "#F8D7DA" };
      default:
        return { color: "#6C757D", bgColor: "#F8F9FA" };
    }
  };

  const getProgressPercentage = (record: WorkOrderData): number => {
    if (!record.workOrderLines || record.workOrderLines.length === 0) return 0;

    const totalWeightage = record.workOrderLines.reduce(
      (sum, line) => sum + (line.weightagePercentage || 0),
      0
    );
    const completedWeightage = record.workOrderLines.reduce(
      (sum, line) =>
        sum +
        ((line.completionPercentage || 0) * (line.weightagePercentage || 0)) /
          100,
      0
    );

    return totalWeightage > 0
      ? Math.round((completedWeightage / totalWeightage) * 100)
      : 0;
  };

  const handleEdit = (record: any) => {
    // Navigate to edit mode
    navigate(`/work-order-creation/?edit=${record?.workOrderId}`);
  };

  const handleSubmitForApproval = async (record: any) => {
    Swal.fire({
      title: "Submit for Approval",
      text: "Are you sure you want to submit this Work Order?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, submit it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Create FormData with workBoqId and status
          const formData = {
            workOrderId: record.workOrderId,
            status: "Submitted",
          };

          const _formData = new FormData();
          _formData.append("workOrder", JSON.stringify(formData));

          // Call the update API
          const response: any = await workOrderApis.updateWorkOrder(_formData);

          if (response && response.status) {
            message.success("Work Order submitted successfully!");
            Swal.fire({
              icon: "success",
              title: "Submitted!",
              text:
                response?.data?.message ||
                "Work Order has been submitted for approval.",
              showConfirmButton: true,
              confirmButtonText: "OK",
            }).then(() => {
              // Trigger refresh by toggling the refresh state
              setRefreshToggle(!refreshToggle);
            });
          } else {
            throw new Error(response?.message || "Failed to submit BOQ");
          }
        } catch (error: any) {
          console.error("Error submitting Work Order:", error);
          message.error("Failed to submit Work Order. Please try again.");
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error.message || "Failed to submit BOQ. Please try again.",
          });
        }
        // const updated = updateWorkRequest(record.id, {
        //   status: WorkRequestStatus.PendingApproval,
        // });
        // if (updated) {
        //   Swal.fire(
        //     "Submitted!",
        //     "Work Request has been submitted for approval.",
        //     "success"
        //   );
        //   loadWorkRequests(); // Refresh the list
        // } else {
        //   Swal.fire("Error!", "Failed to submit work request.", "error");
        // }
      }
    });
  };

  const columns = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 220,
      fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`woStatus-${record?.workOrderId}`}
              onClick={() => {
                if (record?.status === WorkOrderStatus.Draft) {
                  navigate(`/work-order-creation/?edit=${record?.workOrderId}`);
                } else {
                  navigate(`/work-order-view/${record.workOrderId}`);
                }
              }}
            >
              {text}
            </span>
            {/* <TooltipComponent
              iconClass="info-circle text-info"
              icon={record?.status === WorkOrderStatus.Draft ? "info" : ""}
              id={`woStatus-${record?.id}`}
              direction="right"
              text={
                record?.status === WorkOrderStatus.Draft
                  ? "Click To Continue Work Order Creation"
                  : "Click To View Work Order Details"
              }
              containerClassName={"mt-1"}
            /> */}
          </>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 250,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`title-tooltip-${index}`}>
              {text?.length > 30 ? `${text?.substring(0, 27)}... ` : text}
            </span>
            {text?.length > 30 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`title-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Site / Project",
      key: "siteProject",
      width: 200,
      render: (text: any, record: any) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          {/* <div className="text-muted small">{record.projectName || "N/A"}</div> */}
        </div>
      ),
    },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 200,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`contractor-tooltip-${index}`}>
              {text?.length > 25 ? `${text?.substring(0, 18)}... ` : text}
            </span>
            {text?.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`contractor-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Contract Type",
      dataIndex: "contractType",
      key: "contractType",
      width: 150,
    },
    {
      title: "contractDate",
      dataIndex: "contractDate",
      key: "contractDate",
      width: 180,
      render: (date: any, record: any) => {
        return <span>{dayjs(record.contractDate).format("DD-MMM-YYYY")}</span>;
      },
    },

    {
      title: "Created On / By",
      key: "createdInfo",
      width: 180,
      render: (text: any, record: any) => (
        <div>
          <div>{FormatDate(record.createdAt)}</div>
          <div className="text-muted small">{record.createdByName}</div>
        </div>
      ),
    },

    {
      title: "WO Value (Excl. Tax)",
      dataIndex: "totalValue",
      key: "totalValue",
      width: 180,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, "INR")}</span>
        </div>
      ),
    },
    {
      title: "Tax",
      dataIndex: "taxAmount",
      key: "taxAmount",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, "INR")}</span>
        </div>
      ),
    },
    {
      title: "Gross Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 180,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span className="fw-bold">{formatCurrency(text, "INR")}</span>
        </div>
      ),
    },
    // {
    //   title: "Paid To Date",
    //   key: "paidToDate",
    //   width: 150,
    //   render: (text: any, record: any) => (
    //     <div style={{ textAlign: "right" }}>
    //       <span>{formatCurrency(getPaidAmount(record), "INR")}</span>
    //     </div>
    //   ),
    // },
    {
      title: "Outstanding",
      key: "outstandingAmount",
      dataIndex: "outstandingAmount",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, "INR")}</span>
        </div>
      ),
    },
    // {
    //   title: "Retention Balance",
    //   key: "retentionBalance",
    //   width: 150,
    //   render: (text: any, record: any) => {
    //     const retentionAmount =
    //       (record.grossValue *
    //         (record.paymentConditions?.retentionAmount?.percentageOfRABill ||
    //           5)) /
    //       100;
    //     return (
    //       <div style={{ textAlign: "right" }}>
    //         <span>{formatCurrency(retentionAmount, "INR")}</span>
    //       </div>
    //     );
    //   },
    // },
    // {
    //   title: "On-Hold Balance",
    //   key: "onHoldBalance",
    //   width: 150,
    //   render: (text: any, record: any) => (
    //     <div style={{ textAlign: "right" }}>
    //       <span>
    //         {formatCurrency(
    //           record.paymentConditions?.onHoldAmount?.holdAmountValue || 0,
    //           "INR"
    //         )}
    //       </span>
    //     </div>
    //   ),
    // },
    // {
    //   title: "Progress %",
    //   key: "progressPercentage",
    //   width: 120,
    //   render: (text: any, record: any) => {
    //     const progress = getProgressPercentage(record);
    //     return (
    //       <div style={{ textAlign: "center" }}>
    //         <span
    //           className={`badge ${
    //             progress > 75
    //               ? "bg-success"
    //               : progress > 50
    //               ? "bg-warning"
    //               : progress > 25
    //               ? "bg-info"
    //               : "bg-secondary"
    //           }`}
    //         >
    //           {progress}%
    //         </span>
    //       </div>
    //     );
    //   },
    // },
    // {
    //   title: "Age (Days)",
    //   key: "age",
    //   width: 100,
    //   render: (text: any, record: any) => {
    //     const createdDate = new Date(record.createdAt);
    //     const currentDate = new Date();
    //     const ageInDays = Math.floor(
    //       (currentDate.getTime() - createdDate.getTime()) /
    //         (1000 * 60 * 60 * 24)
    //     );
    //     return (
    //       <div style={{ textAlign: "center" }}>
    //         <span>{ageInDays}</span>
    //       </div>
    //     );
    //   },
    // },
    {
      title: "Owner",
      dataIndex: "workOrderOwnerName",
      key: "workOrderOwnerName",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      fixed: "right",
      render: (status: WorkOrderStatus) => {
        const statusStyle = getStatusColor(status);
        return (
          <div className="justify-start align-center">
            <span
              className="badge"
              style={{
                color: statusStyle.color,
                backgroundColor: statusStyle.bgColor,
                border: `1px solid ${statusStyle.color}`,
                fontSize: "12px",
                padding: "4px 8px",
              }}
            >
              {status}
            </span>
          </div>
        );
      },
    },
    // {
    //   title: "Last Updated",
    //   dataIndex: "updatedAt",
    //   key: "updatedAt",
    //   width: 150,
    //   render: (date: any, record: any) => (
    //     <span>{FormatDate(date || record.createdAt)}</span>
    //   ),
    // },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_: any, record: any) => (
        <Space wrap>
          {record.status === "Draft" && (
            <>
              <Button
                type="link"
                size="small"
                icon={<Icon name="edit" />}
                onClick={() => handleEdit(record)}
              >
                Edit
              </Button>

              <Button
                type="link"
                size="small"
                icon={<Icon name="send" />}
                onClick={() => handleSubmitForApproval(record)}
              >
                Submit
              </Button>
            </>
          )}

          {/* {(record.status === "Draft" || record.status === "Rejected") && (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<Icon name="trash" />}
                  onClick={() => handleDelete(record)}
                >
                  Delete
                </Button>
              )} */}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    // Load mock data
    setWidgetData(mockWorkOrderWidgetData);
    setWorkOrderStatus(mockWorkOrderStatusOptions);
    setContractorOptions(mockContractorOptions);
    setContractTypeOptions(mockContractTypeOptions);
  }, []);

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  // KPI Filter Change Handler
  const handleKPIFilterChange = (filterValue: string) => {
    setSelectedKPIFilter(filterValue);
  };

  // Filter Functions
  const handleFilterDrawerOpen = () => {
    setTempFilters({ ...filters });
    form.setFieldsValue(filters);
    setFilterDrawerVisible(true);
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    form.resetFields();
  };

  const handleApplyFilters = () => {
    form
      .validateFields()
      .then((values) => {
        // Process form values to match our filter state structure
        const processedFilters: FilterState = {
          workOrderNumber: values.workOrderNumber?.trim() || undefined,
          workOrderTitle: values.workOrderTitle?.trim() || undefined,
          status:
            values.status && values.status.length > 0
              ? values.status
              : undefined,
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          contractType:
            values.contractType && values.contractType.length > 0
              ? values.contractType
              : undefined,
          createdDateRange: values.createdDateRange || undefined,
          releasedDateRange: values.releasedDateRange || undefined,
          grossValueRange: {
            min: values.grossValueMin || undefined,
            max: values.grossValueMax || undefined,
          },
          workOrderOwner: values.workOrderOwner?.trim() || undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        // Remove empty grossValueRange
        if (
          processedFilters.grossValueRange &&
          !processedFilters.grossValueRange.min &&
          !processedFilters.grossValueRange.max
        ) {
          delete processedFilters.grossValueRange;
        }

        setFilters(processedFilters);
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    form.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName.trim()]: { ...filters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setFilters(savedFilter);
      setTempFilters(savedFilter);
      form.setFieldsValue(savedFilter);
    }
  };

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="view-work-order"
        title="Work Orders"
        columns={columns}
        refresh={refreshToggle}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <WorkOrderAside updateSm={setSm} sm={sm} />
          </div>
        }
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
        extra={{
          tableScrollX: 2500,
          dateRange: false,
          excelExport: true,
          dataFilterTootip: "Filtering based on Work Order created date",
        }}
        // widget={{
        //   isActive: true,
        //   compactGrid: true,
        //   kpiFilter: {
        //     isActive: true,
        //     options: kpiFilterOptions,
        //     selectedFilter: selectedKPIFilter,
        //     onFilterChange: handleKPIFilterChange,
        //   },
        //   list: getWidgetsByFilter(selectedKPIFilter),
        // }}
      />

      <Drawer
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Advanced Filters</span>
            <Space>
              <Badge count={getActiveFilterCount()} size="small">
                <Button size="small" type="text" icon={<FilterOutlined />} />
              </Badge>
            </Space>
          </div>
        }
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Option key={filterName} value={filterName}>
                      {filterName}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={form} layout="vertical" initialValues={filters}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="workOrderNumber" label="Work Order Number">
                <Input
                  placeholder="Enter WO Number"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderTitle" label="Work Order Title">
                <Input
                  placeholder="Enter WO Title"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="status" label="Status">
                <Select
                  mode="multiple"
                  placeholder="Select Status"
                  options={workOrderStatus}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="siteId" label="Site">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="contractorName" label="Contractor">
                <Select
                  mode="multiple"
                  placeholder="Select Contractors"
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="contractType" label="Contract Type">
                <Select
                  mode="multiple"
                  placeholder="Select Contract Types"
                  options={contractTypeOptions}
                  allowClear
                  showSearch
                  filterOption={(input: any, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderOwner" label="Work Order Owner">
                <Input
                  placeholder="Enter Owner Name"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider orientation="left" orientationMargin="0">
                Date Ranges
              </Divider>
            </Col>

            <Col span={24}>
              <Form.Item name="createdDateRange" label="Created Date Range">
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={["Start Date", "End Date"]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="releasedDateRange" label="Released Date Range">
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={["Start Date", "End Date"]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider orientation="left" orientationMargin="0">
                Value Range
              </Divider>
            </Col>

            <Col span={12}>
              <Form.Item name="grossValueMin" label="Min Gross Value">
                <Input
                  type="number"
                  placeholder="Minimum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="grossValueMax" label="Max Gross Value">
                <Input
                  type="number"
                  placeholder="Maximum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default ViewWorkOrder;
